import { HttpStatusCode } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Accept } from 'react-dropzone';
import { SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../shared/Buttons';
import ImageUpload from '../../../../../shared/ImageUpload';
import {
  Asset,
  CashAdvanceInvoiceDocumentSubType,
  DocumentUploadPresignedUrlsRequest,
  Entity,
  PresignedUrlObject,
} from '../../../../../types/apiTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import { useUser } from '../../../../../utils/UserContext';
import { getFileExtension } from '../../../../../utils/FileUtils';
import { LoanRequest, Trailer, Truck } from '../../../../../types/dbTypes';
import { InputNumber, Slider, Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { formatNumberCommas } from '../../../../../utils/CurrencyUtils';
import MaskedDatePicker from '../../../../signup/customers/MaskedDatePicker';

interface RequestCashAdvanceProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

const acceptFiles: Accept = {
  'image/*': ['.png', '.jpeg', '.jpg', '.heic', '.heif'],
  'application/pdf': ['.pdf'],
};

// ─── Cash Advance Form ────────────────────────────────────────────────────────

interface CashAdvanceFormData {
  invoices: File[];
  amount: number;
  reason: string;
  vendor_id: string;
  invoice_number: string;
  po_number: string;
  invoice_date: Date | null;
  asset_id: string | null;
  asset_type: string | null;
}

function CashAdvanceForm({
  loading,
  loanMax,
  availableCashAdvance,
  budgetedCashReserves,
  assets,
  loadingAssets,
  vendors,
  searchingVendors,
  searchVendors,
  onSubmit,
  loadingCreateLoanRequest,
}: {
  loading: boolean;
  loanMax: number;
  availableCashAdvance: number;
  budgetedCashReserves: number;
  assets: Asset[];
  loadingAssets: boolean;
  vendors: any[];
  searchingVendors: boolean;
  searchVendors: (query: string) => void;
  onSubmit: (data: CashAdvanceFormData, invoices: File[]) => Promise<void>;
  loadingCreateLoanRequest: boolean;
}) {
  const [invoices, setInvoices] = useState<File[]>([]);
  const navigate = useNavigate();

  const vendorSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const debouncedSearchVendors = useCallback(
    (value: string) => {
      if (vendorSearchTimerRef.current) {
        clearTimeout(vendorSearchTimerRef.current);
      }
      vendorSearchTimerRef.current = setTimeout(() => {
        searchVendors(value);
      }, 300);
    },
    [searchVendors]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CashAdvanceFormData>({
    defaultValues: {
      amount: 100,
      invoices: [],
      vendor_id: '',
      invoice_date: null,
      asset_id: null,
      asset_type: null,
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    register('vendor_id', { required: 'Vendor is required' });
    register('invoice_date', { required: 'Invoice date is required' });
    register('invoices', {
      required: 'At least one invoice document is required',
    });
  }, [register]);

  useEffect(() => {
    if (budgetedCashReserves > 0) {
      setValue(
        'amount',
        Math.min(budgetedCashReserves, availableCashAdvance || Infinity)
      );
    }
  }, [budgetedCashReserves, availableCashAdvance, setValue]);

  const handleDrop = (acceptedFiles: File[]) => {
    setInvoices((prev) => [...prev, ...acceptedFiles]);
    setValue('invoices', [...(watch('invoices') || []), ...acceptedFiles]);
  };

  const findAssetTypeByAssetId = (asset_id: string) => {
    return (
      assets.find(
        (asset) =>
          (asset?.asset_type === 'truck'
            ? (asset as Truck)?.truck_id
            : asset?.asset_type === 'trailer' &&
              (asset as Trailer)?.trailer_id) === asset_id
      )?.asset_type ?? null
    );
  };

  const generateAssetDisplayName = (asset: Asset) => {
    if (asset?.asset_type === 'truck') {
      return `${(asset as Truck)?.truck_display_name} - ${(asset as Truck)?.vehicle_id_number}`;
    } else {
      return `${(asset as Trailer)?.trailer_display_name} - ${(asset as Trailer)?.vehicle_id_number}`;
    }
  };

  const retrieveTruckIdOrTrailerId = (asset: Asset) => {
    if (asset?.asset_type === 'truck') {
      return (asset as Truck)?.truck_id;
    } else {
      return (asset as Trailer)?.trailer_id;
    }
  };

  const handleFormSubmit: SubmitHandler<CashAdvanceFormData> = async (data) => {
    if (invoices.length === 0) {
      toast.error('Please upload at least one invoice document');
      return;
    }
    if (data?.amount < budgetedCashReserves + 100) {
      toast.error(
        `You have $${formatNumberCommas(budgetedCashReserves)} in cash reserves, which can be used to pay for this invoice.`
      );
      return;
    }
    await onSubmit(data, invoices);
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className="flex w-full flex-col bg-white pt-5"
    >
      <div className="flex w-full flex-col items-center gap-y-6 px-4">
        <div className="flex w-full flex-col items-center justify-center gap-4 lg:flex-row">
          <div className="flex w-full flex-col items-start gap-y-2 rounded-[4px] border-[1px] border-bordergray p-3 lg:w-[30%]">
            <p className="text-[16px] text-graytertiary">
              Total cash advance limit:
            </p>
            <p className="text-[18px] font-[600] text-graytertiary">
              ${formatNumberCommas(loanMax)}
              <span className="text-[16px] leading-[28px] text-graysecondary">
                .{(loanMax % 1 || 0).toFixed(2).split('.')[1] || '00'}
              </span>
            </p>
          </div>
          <div className="flex w-full flex-col items-start gap-y-2 rounded-[4px] border-[1px] border-bordergray p-3 lg:w-[30%]">
            <p className="text-[16px] text-graytertiary">Available amount:</p>
            <p className="text-[18px] font-[600] text-primary">
              ${formatNumberCommas(availableCashAdvance)}
              <span className="text-[16px] leading-[28px] text-graysecondary">
                .
                {(availableCashAdvance % 1 || 0).toFixed(2).split('.')[1] ||
                  '00'}
              </span>
            </p>
          </div>
        </div>
        <div className="flex w-full flex-col items-center space-y-1">
          <p className="text-[14px] text-[#7D8191]">
            Upload an invoice (.pdf, .jpg, .jpeg, .png, .heic, .heif)
          </p>
          <div className="w-full border-[1px] border-[#EAECF0] bg-[#808080] bg-opacity-[8%] p-3 md:w-[60%]">
            <ImageUpload
              onDrop={(files) => handleDrop(files)}
              accept={acceptFiles}
              multiple
              promptText={
                <>
                  Drag files here or{' '}
                  <span className="cursor-pointer text-blue-500 hover:underline">
                    browse
                  </span>{' '}
                  to choose invoice files
                </>
              }
              uploadedFilesLabel="Uploaded Files"
              successMessage={
                invoices.length > 0 ? 'Documents uploaded successfully!' : ''
              }
              errorMessage={
                invoices.length === 0 && errors.invoices
                  ? 'At least one invoice document is required'
                  : ''
              }
              uploadedFileNames={invoices.map((file) => file.name)}
            />
          </div>
        </div>
        <div className="flex w-full flex-col items-start gap-y-6 md:w-[60%]">
          <div className="w-full">
            <label
              htmlFor="reason"
              className="block text-[14px] font-medium text-[#858897]"
            >
              What is the reason you need a cash advance?
            </label>
            <textarea
              placeholder="Enter your reason here..."
              {...register('reason', {
                required: 'Cash advance reason is required',
                maxLength: {
                  value: 254,
                  message: 'Maximum length is 254 characters',
                },
              })}
              id="reason"
              rows={4}
              className={`mt-1 block w-full border px-3 py-2 ${errors.reason ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
            />
            {errors.reason && (
              <p className="mt-1 text-sm text-red-500">
                {errors.reason.message}
              </p>
            )}
          </div>

          <div className="w-full">
            <label
              htmlFor="vendor"
              className="block text-[14px] font-medium text-[#858897]"
            >
              Select Vendor or Merchant on Invoice
            </label>
            <Select
              id="vendor"
              className={`mt-1 w-full ${errors.vendor_id ? 'border-red-500' : ''}`}
              placeholder="Search for a vendor"
              showSearch
              filterOption={false}
              onSearch={debouncedSearchVendors}
              onChange={(value) => {
                setValue('vendor_id', value, { shouldValidate: true });
              }}
              loading={searchingVendors}
              suffixIcon={
                searchingVendors ? (
                  <LoadingOutlined
                    style={{ fontSize: 20, color: '#1D213B' }}
                    spin
                  />
                ) : undefined
              }
              notFoundContent={
                searchingVendors ? 'Searching...' : 'No vendors found'
              }
              options={[
                { value: 'unknown', label: 'Unknown' },
                ...vendors.map((vendor) => ({
                  value: vendor.vendor_id,
                  label: `${vendor.business_name} - ${vendor.address_st_number} ${vendor.address_st_name}, ${vendor.address_city}, ${vendor.address_state} ${vendor.address_zip}`,
                })),
              ]}
              status={errors.vendor_id ? 'error' : undefined}
            />
            {errors.vendor_id && (
              <p className="mt-1 text-sm text-red-500">Vendor is required</p>
            )}
          </div>
          <div className="w-full">
            <label
              htmlFor="invoice_number"
              className="block text-[14px] font-medium text-[#858897]"
            >
              Invoice Number
            </label>
            <input
              type="text"
              id="invoice_number"
              placeholder="Enter invoice number"
              {...register('invoice_number', {
                required: 'Invoice number is required',
              })}
              className={`mt-1 block w-full border px-3 py-2 ${errors.invoice_number ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
            />
            {errors.invoice_number && (
              <p className="mt-1 text-sm text-red-500">
                {errors.invoice_number.message}
              </p>
            )}
          </div>

          <div className="w-full">
            <label
              htmlFor="po_number"
              className="block text-[14px] font-medium text-[#858897]"
            >
              PO Number (Optional)
            </label>
            <input
              type="text"
              id="po_number"
              placeholder="Enter PO number (if applicable)"
              {...register('po_number')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm"
            />
          </div>

          <div className="w-full">
            <label
              htmlFor="invoice_date"
              className="block text-[14px] font-medium text-[#858897]"
            >
              Date of Invoice
            </label>
            <MaskedDatePicker
              id="invoice_date"
              className={`mt-1 w-full ${errors.invoice_date ? 'border-red-500' : ''}`}
              placeholder="Select invoice date (MM/DD/YYYY)"
              onChange={(date) => {
                setValue('invoice_date', date?.toDate() || null, {
                  shouldValidate: true,
                });
              }}
              format="MM/DD/YYYY"
              placement="topLeft"
              status={errors.invoice_date ? 'error' : undefined}
            />
            {errors.invoice_date && (
              <p className="mt-1 text-sm text-red-500">
                Invoice date is required
              </p>
            )}
          </div>

          <div className="w-full">
            <label
              htmlFor="asset_id"
              className="block text-[14px] font-medium text-[#858897]"
            >
              Select Truck or Trailer (Optional)
            </label>
            <Select
              id="asset_id"
              className="mt-1 w-full"
              placeholder="Select a truck or trailer"
              onChange={(value) => {
                setValue('asset_id', value);
                setValue('asset_type', findAssetTypeByAssetId(value));
              }}
              loading={loadingAssets}
              notFoundContent={
                loadingAssets ? 'Loading assets...' : 'No assets found'
              }
              options={assets.map((asset) => ({
                value: retrieveTruckIdOrTrailerId(asset),
                label: generateAssetDisplayName(asset),
              }))}
            />
            {errors.asset_id && (
              <p className="mt-1 text-sm text-red-500">
                {errors.asset_id.message}
              </p>
            )}
          </div>

          <div className="mb-4 w-full">
            <label
              htmlFor="amount"
              className="block text-[14px] font-medium text-[#858897]"
            >
              Cash Advance Amount
            </label>
            {availableCashAdvance < 100 ? (
              <div className="mt-1">
                <InputNumber
                  id="amount"
                  className="w-full"
                  disabled
                  value={0}
                  formatter={() => '$ 0.00'}
                />
                <p className="mt-2 text-sm text-red-500">
                  You are ineligible for a cash advance as your available
                  advance limit is less than $100
                </p>
              </div>
            ) : (
              <>
                <InputNumber
                  id="amount"
                  className="mt-1 w-full"
                  formatter={(value) => {
                    if (value === null || value === undefined) return '$ ';
                    return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  }}
                  parser={(value) => {
                    const parsed = value!.replace(/\$\s?|(,*)/g, '');
                    return parsed === '' ? 100 : Number(parsed);
                  }}
                  precision={2}
                  min={100}
                  max={availableCashAdvance}
                  placeholder="Enter amount"
                  defaultValue={100}
                  value={watch('amount')}
                  onChange={(value) => setValue('amount', value as number)}
                  controls={false}
                />
                <div className="mt-4 w-full">
                  <Slider
                    min={100}
                    styles={{ track: { background: '#F7A826' } }}
                    max={availableCashAdvance}
                    onChange={(value) => setValue('amount', value)}
                    value={watch('amount') ?? 100}
                    tooltip={{
                      formatter: (value?: number) =>
                        value ? `$${formatNumberCommas(value)}` : '$0',
                    }}
                  />
                  <div className="mt-1 flex justify-between text-xs text-gray-500">
                    <span>$100</span>
                    <span>${formatNumberCommas(availableCashAdvance)}</span>
                  </div>
                </div>
              </>
            )}
            {errors.amount && (
              <p className="mt-1 text-sm text-red-500">
                {errors.amount.message}
              </p>
            )}
          </div>
        </div>
        <div className="flex w-full flex-col items-center justify-between gap-4 border-t-[1px] border-[#EAECF0] py-4 align-middle md:flex-row">
          <NeutralButton
            onClick={() => navigate(-1)}
            addClass="rounded-full w-full md:w-[250px] h-[48px]"
          >
            Cancel
          </NeutralButton>
          <AccentButton
            addClass={`h-[48px] w-full md:w-[250px] rounded-full`}
            submit={true}
            disabled={
              Object.keys(errors).length > 0 ||
              invoices.length === 0 ||
              loading ||
              loadingCreateLoanRequest
            }
          >
            {!loading && !loadingCreateLoanRequest ? (
              'Submit Request'
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading || loadingCreateLoanRequest}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="flex h-full w-full justify-center"
              />
            )}
          </AccentButton>
        </div>
      </div>
    </form>
  );
}

// ─── Parent Component ─────────────────────────────────────────────────────────

function RequestCashAdvance({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: RequestCashAdvanceProps) {
  const { callApi, callProtectedApi } = useApi();
  const { userId, userRecord } = useUser();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingCreateLoanRequest, setLoadingCreateLoanRequest] =
    useState(false);
  // const [advanceType] = useState<LoanType>(
  //   locationState?.advanceType ?? LoanType.CASH_ADVANCE
  // );

  // Cash advance state
  const [loanMax, setLoanMax] = useState(0);
  const [availableCashAdvance, setAvailableCashAdvance] = useState(0);
  const [budgetedCashReserves, setBudgetedCashReserves] = useState(0);
  const [vendors, setVendors] = useState<any[]>([]);
  const [searchingVendors, setSearchingVendors] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assets, setAssets] = useState<Asset[]>([]);

  // const isFuelAdvance = advanceType === LoanType.FUEL_ADVANCE;

  // ─── API Helpers ──────────────────────────────────────────────────────

  const justInTimeLimitCheck = async (customer_id: string) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/credit/${customer_id}/check`,
        'get',
        {},
        "Error occurred while checking your account's cash advance limit."
      );
      const data = response?.data?.data?.cashAdvanceLimit;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        response?.status === HttpStatusCode.Ok
      ) {
        if (
          data?.loan_max === 0 ||
          data?.available_cash_advance_limit === 0 ||
          data?.is_cash_advance_active === false ||
          data?.budgeted_cash_reserves === 0
        ) {
          toast.error('You are not eligible for a cash advance at this time.');
          navigate('/dashboard/home');
          return;
        }
        if (
          data?.available_cash_advance_limit <= data?.budgeted_cash_reserves
        ) {
          toast.error(
            'You are not eligible for a cash advance at this time due to insufficient income history. Please try again later.'
          );
          navigate('/dashboard/home');
          return;
        }
        setLoanMax(data?.loan_max);
        setBudgetedCashReserves(data?.budgeted_cash_reserves);
        setAvailableCashAdvance(data?.available_cash_advance_limit);
      }
    } catch (e: any) {
      console.error(
        "Error occurred while checking your account's cash advance limit: ",
        e
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async (customer_id: string) => {
    setLoadingAssets(true);
    try {
      const response = await callProtectedApi(
        `/assets/${customer_id}`,
        'get',
        {},
        'Error occurred while fetching your trucks & trailers.'
      );
      const data = response?.data?.data?.assets;
      if (data && data?.length > 0 && response?.status === HttpStatusCode.Ok) {
        setAssets(data);
      }
    } catch (e: any) {
      console.error(
        'Error occurred while fetching your trucks & trailers: ',
        e
      );
    } finally {
      setLoadingAssets(false);
    }
  };

  const searchVendors = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim().length < 2) return;
    setSearchingVendors(true);
    try {
      const response = await callProtectedApi(
        `/vendors/search/${userId ?? ''}`,
        'post',
        { searchQuery },
        'Error occurred while searching vendors.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        setVendors(response?.data?.data?.vendors || []);
      }
    } catch (error: any) {
      console.error('Error searching vendors:', error);
    } finally {
      setSearchingVendors(false);
    }
  };

  // const checkFuelAdvanceEligibility = async (customer_id: string) => {
  //   try {
  //     const response = await callProtectedApi(
  //       `/credit/${customer_id}/fuel-advance/eligibility`,
  //       'get',
  //       {},
  //       'Error checking fuel advance eligibility.'
  //     );
  //     const data = response?.data?.data;
  //     if (data && response?.status === HttpStatusCode.Ok) {
  //       setFuelAdvanceEligibility(data);
  //       if (!data.eligible) {
  //         toast.error(
  //           data.reason ||
  //             'You are not eligible for a fuel advance at this time.'
  //         );
  //       }
  //     }
  //   } catch (e: any) {
  //     console.error('Error checking fuel advance eligibility:', e);
  //   }
  // };

  const uploadInvoice = async (data: LoanRequest, invoices: File[]) => {
    try {
      const fileExtensions = invoices.map((file) => getFileExtension(file));
      const responseDocUrl = await callProtectedApi(
        `/documents/cashAdvanceInvoice/${data?.request_id}`,
        'post',
        {
          document_count: invoices.length,
          file_extensions: fileExtensions,
          document_sub_type: CashAdvanceInvoiceDocumentSubType.Invoice,
        } as DocumentUploadPresignedUrlsRequest,
        'Error occurred while uploading your invoices.'
      );
      const urls: PresignedUrlObject[] =
        responseDocUrl?.data?.data?.document_urls;
      const uploadPromises = invoices.map((file, index) => {
        return callApi(
          '',
          'put',
          file,
          'Error occurred while uploading invoice file.',
          { 'Content-Type': file.type },
          urls[index]?.presignedUrl,
          5
        ).then((result) => {
          if (result?.status === HttpStatusCode.Ok) return true;
          toast.error(`Failed to upload ${file.name}. HTTP Error ` + result);
          return false;
        });
      });
      const results = await Promise.all(uploadPromises);
      return results.every((result) => result === true);
    } catch (error: any) {
      console.error('Error uploading invoices:', error);
      toast.error('Failed to upload invoices');
      return false;
    }
  };

  // const uploadFuelAdvanceDocs = async (
  //   requestId: string,
  //   bolFiles: File[],
  //   rateConFiles: File[]
  // ) => {
  //   const uploadDocSet = async (
  //     files: File[],
  //     subType: CashAdvanceInvoiceDocumentSubType | FuelAdvanceDocumentSubType
  //   ) => {
  //     if (files.length === 0) return true;
  //     const fileExtensions = files.map((file) => getFileExtension(file));
  //     const responseDocUrl = await callProtectedApi(
  //       `/documents/cashAdvanceInvoice/${requestId}`,
  //       'post',
  //       {
  //         document_count: files.length,
  //         file_extensions: fileExtensions,
  //         document_sub_type: subType,
  //       } as DocumentUploadPresignedUrlsRequest,
  //       'Error uploading fuel advance documents.'
  //     );
  //     const urls: PresignedUrlObject[] =
  //       responseDocUrl?.data?.data?.document_urls;
  //     const uploadPromises = files.map((file, index) =>
  //       callApi(
  //         '',
  //         'put',
  //         file,
  //         'Error uploading file.',
  //         { 'Content-Type': file.type },
  //         urls[index]?.presignedUrl,
  //         5
  //       ).then((result) => result?.status === HttpStatusCode.Ok)
  //     );
  //     const results = await Promise.all(uploadPromises);
  //     return results.every(Boolean);
  //   };

  //   const bolSuccess = await uploadDocSet(
  //     bolFiles,
  //     FuelAdvanceDocumentSubType.BOL
  //   );
  //   const rateConSuccess = await uploadDocSet(
  //     rateConFiles,
  //     FuelAdvanceDocumentSubType.RateConfirmation
  //   );
  //   return bolSuccess && rateConSuccess;
  // };

  // ─── Submit Handlers ──────────────────────────────────────────────────

  const handleCashAdvanceSubmit = async (
    data: CashAdvanceFormData,
    invoices: File[]
  ) => {
    setLoadingCreateLoanRequest(true);
    try {
      const response = await callProtectedApi(
        `/credit`,
        'post',
        {
          customer_id: userId,
          amount: data?.amount,
          reason: data?.reason,
          vendor_id:
            data?.vendor_id === 'unknown' ? undefined : data?.vendor_id,
          invoice_number: data?.invoice_number,
          po_number:
            data?.po_number && data?.po_number?.length > 0
              ? data?.po_number
              : undefined,
          asset_id:
            data?.asset_id && data?.asset_id?.length > 0
              ? data?.asset_id
              : undefined,
          asset_type:
            data?.asset_type && data?.asset_type?.length > 0
              ? data?.asset_type
              : undefined,
          date_of_invoice: data?.invoice_date?.toISOString(),
        },
        'Error occurred while creating your cash advance request.'
      );
      const loanRequest = response?.data?.data?.loanRequest;
      if (
        loanRequest &&
        Object.entries(loanRequest)?.length > 0 &&
        response?.status === HttpStatusCode.Ok
      ) {
        const allSuccessful = await uploadInvoice(loanRequest, invoices);
        if (allSuccessful) {
          toast.success('Cash advance request submitted successfully!');
          navigate(-1);
        } else {
          toast.error('Failed to upload invoice document(s).');
        }
      }
    } catch (e: any) {
      console.error('Error creating cash advance request:', e);
    } finally {
      setLoadingCreateLoanRequest(false);
    }
  };

  // const handleFuelAdvanceSubmit = async (
  //   amount: number,
  //   loadValue: number,
  //   deliveryDate: Dayjs,
  //   bolFiles: File[],
  //   rateConFiles: File[]
  // ) => {
  //   setLoadingCreateLoanRequest(true);
  //   try {
  //     const response = await callProtectedApi(
  //       `/credit`,
  //       'post',
  //       {
  //         customer_id: userId,
  //         amount,
  //         reason: 'Fuel Advance',
  //         type: LoanType.FUEL_ADVANCE,
  //         load_value: loadValue,
  //         delivery_date: deliveryDate.toISOString(),
  //         vendor_id: null,
  //         po_number: null,
  //         invoice_number: null,
  //         date_of_invoice: null,
  //         asset_id: null,
  //         asset_type: null,
  //       },
  //       'Error creating fuel advance request.'
  //     );
  //     const loanRequest = response?.data?.data?.loanRequest;
  //     if (
  //       loanRequest &&
  //       Object.entries(loanRequest)?.length > 0 &&
  //       response?.status === HttpStatusCode.Ok
  //     ) {
  //       const allSuccessful = await uploadFuelAdvanceDocs(
  //         loanRequest.request_id,
  //         bolFiles,
  //         rateConFiles
  //       );
  //       if (allSuccessful) {
  //         toast.success('Fuel advance request submitted successfully!');
  //         navigate('/dashboard/banking/cash-advances/requested');
  //       } else {
  //         toast.error('Failed to upload fuel advance document(s).');
  //       }
  //     }
  //   } catch (e: any) {
  //     console.error('Error creating fuel advance request:', e);
  //   } finally {
  //     setLoadingCreateLoanRequest(false);
  //   }
  // };

  // ─── Effects ──────────────────────────────────────────────────────────

  useEffect(() => {
    justInTimeLimitCheck(userId ?? '');
    fetchAssets(userId ?? '');
    // Fuel advances disabled — no eligibility check on mount
    // if (locationState?.advanceType === LoanType.FUEL_ADVANCE) {
    //   checkFuelAdvanceEligibility(userId ?? '');
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      userRecord?.userType === Entity.Customer &&
      !userRecord?.is_cash_advance_active
    ) {
      toast.error('You are not eligible for a cash advance at this time.');
      navigate('/dashboard/home');
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRecord]);

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="flex h-full w-full flex-col overflow-y-auto">
      <div className="mx-auto w-full max-w-[1148px] px-4 pb-8 pt-4">
        {!loading ? (
          <>
            {/* Type Selector — Fuel Advances disabled */}
            {/* <div className="flex w-full items-center justify-center gap-4 px-4 pb-6 pt-5">
              <button
                type="button"
                className={`rounded-lg px-6 py-2 text-sm font-medium transition-colors ${!isFuelAdvance ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                onClick={() => {
                  setAdvanceType(LoanType.CASH_ADVANCE);
                  navigate('/dashboard/banking/cash-advance', {
                    replace: true,
                    state: { advanceType: LoanType.CASH_ADVANCE },
                  });
                }}
              >
                Cash Advance
              </button>
              <button
                type="button"
                className={`rounded-lg px-6 py-2 text-sm font-medium transition-colors ${isFuelAdvance ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                onClick={() => {
                  setAdvanceType(LoanType.FUEL_ADVANCE);
                  checkFuelAdvanceEligibility(userId ?? '');
                  navigate('/dashboard/banking/cash-advance', {
                    replace: true,
                    state: { advanceType: LoanType.FUEL_ADVANCE },
                  });
                }}
              >
                Fuel Advance
              </button>
            </div> */}

            {/* Form — Fuel advance form disabled, always show cash advance */}
            <CashAdvanceForm
              loading={loading}
              loanMax={loanMax}
              availableCashAdvance={availableCashAdvance}
              budgetedCashReserves={budgetedCashReserves}
              assets={assets}
              loadingAssets={loadingAssets}
              vendors={vendors}
              searchingVendors={searchingVendors}
              searchVendors={searchVendors}
              onSubmit={handleCashAdvanceSubmit}
              loadingCreateLoanRequest={loadingCreateLoanRequest}
            />
          </>
        ) : (
          <div className="flex min-h-[300px] w-full items-center justify-center">
            <ClipLoader
              color="#1D213B"
              loading={loading}
              size={60}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="flex h-full w-full justify-center"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default RequestCashAdvance;
